<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import store from '../../../store';
import SkiPricePeriod from '../../../components/ski/skipass/SkiPricePeriod';
import i18n from '../../../i18n';

export default {
  name: 'SkiPricePeriods',
  components: {
    SkiPricePeriod,
  },
  data() {
    return {
      showOldPeriodPrices: false,
    };
  },
  computed: {
    ...mapGetters({
      activePricePeriods: 'skipass/getActivePricePeriods',
      historicallyPricePeriods: 'skipass/getHistoricallyPricePeriods',
      personCategories: 'skipass/getPersonCategories',
    }),
    ...mapState({
      season: state => state.skipass.season,
      periodsLoading: state => state.skipass.season.pricePeriods.itemsLoading,
      periodsError: state => state.skipass.season.pricePeriods.itemsError,
      periodsSaveLoading: state => state.skipass.season.pricePeriods.saveLoading,
      periodsSaveError: state => state.skipass.season.pricePeriods.saveError,
      pricesLoading: state => state.skipass.season.prices.itemsLoading,
      pricesError: state => state.skipass.season.prices.itemsError,
      pricesSaveLoading: state => state.skipass.season.prices.saveLoading,
      pricesSaveError: state => state.skipass.season.prices.saveError,
      personCategoriesLoading: state => state.skipass.season.personCategories.itemsLoading,
      personCategoriesError: state => state.skipass.season.personCategories.itemsError,
    }),
    isNewPeriodActive() {
      return this.activePricePeriods.some(period => period.id === undefined);
    },
    errorOccurred() {
      let errorMessage = '';
      if (this.pricesSaveError) {
        errorMessage = 'Failed to save prices';
      } else if (this.pricesError) {
        errorMessage = 'Price error';
      } else if (this.periodsError) {
        errorMessage = 'Price period error';
      } else if (this.periodsSaveError) {
        errorMessage = 'Failed to save price period';
      }

      return errorMessage;
    },
    hideOrShowOldPeriodsButtonText() {
      return this.showOldPeriodPrices
        ? this.$t('ski.skiPricePeriods.hideOldPricePeriods')
        : this.$t('ski.skiPricePeriods.seeOldPricePeriods', {
          total: Object.keys(this.historicallyPricePeriods).length,
        });
    },
  },
  methods: {
    ...mapActions({
      loadPersonCategories: 'skipass/personCategoriesByIdLoad',
      loadPeriods: 'skipass/periodsWithPricesBySeasonIdLoad',
      setPeriod: 'skipass/setPeriod',
      addNewPeriod: 'skipass/periodAddNew',
      createPeriod: 'skipass/periodCreate',
      createPeriodToAnotherPeriod: 'skipass/periodToAnotherPeriodCreate',
      savePeriod: 'skipass/periodSave',
      removePeriod: 'skipass/periodRemove',
      loadPricesByPeriodId: 'skipass/pricesByPeriodIdLoad',
      setPeriodPrice: 'skipass/setPeriodPrice',
      addNewPrice: 'skipass/priceAddNew',
      createPrice: 'skipass/priceCreate',
      savePrice: 'skipass/priceSave',
      removePrice: 'skipass/priceRemove',
    }),
    createOrUpdatePeriod(period) {
      if (period.id) {
        this.savePeriod(period);
      } else {
        this.createPeriod(period);
      }
    },
    removePeriodByPeriodId(periodId) {
      // eslint-disable-next-line no-alert
      if (!periodId || confirm(i18n.t('ski.skiPricePeriod.removeAreYouSure'))) {
        this.removePeriod(periodId);
      }
    },
    onPeriodChange(payload) {
      if (payload.params.arrivalFrom
        && (payload.params.arrivalFrom > payload.datesItem.arrivalTo
          || !payload.datesItem.arrivalTo
        )
      ) {
        this.setPeriod({
          periodId: payload.periodId,
          params: {
            arrivalTo: payload.params.arrivalFrom,
          },
        });
      }
      this.setPeriod(payload);
    },
    createOrUpdatePrice(periodId, periodPrice) {
      if (periodPrice.id) {
        this.savePrice({
          periodId,
          periodPrice,
        });
      } else {
        this.createPrice({
          periodId,
          periodPrice,
        });
      }
    },
    removePriceByPriceId(periodId, priceId) {
      // eslint-disable-next-line no-alert
      if (!periodId || !priceId || confirm(i18n.t('ski.skiPrice.removeAreYouSure'))) {
        this.removePrice({
          periodId,
          priceId,
        });
      }
    },
    removePricesInDay(periodId, dayPrices, numberOfDays) {
      // eslint-disable-next-line no-alert
      if (!periodId || confirm(i18n.t('ski.skiPrice.removeAllAreYouSure'))) {
        const prices = [
          ...Object.values(dayPrices.personCategories).filter(
            category => category.id !== undefined
              && category.days === Number(numberOfDays),
          ),
        ];
        for (let i = 0; i < prices.length; i += 1) {
          this.removePrice({
            periodId,
            priceId: prices[i].id,
          });
        }
      }
    },
    onPriceChange(payload) {
      this.setPeriodPrice(payload);
    },
  },
  mounted() {
    if (this.season.id) {
      if (!this.pricePeriods || !this.pricePeriods.length) {
        this.loadPeriods(this.season.id);
      }
      if (!this.personCategories || !this.personCategories.length) {
        this.loadPersonCategories(this.season.id);
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    if (to.params.seasonId) {
      store.dispatch('skipass/seasonByIdLoad', to.params.seasonId)
        .finally(() => {
          next();
        });
    }
  },
};
</script>

<template>
  <div>
    <h1 class="d-inline-block">{{ $t('ski.skiPricePeriods.title') }}</h1>
    <v-btn
      :disabled="isNewPeriodActive || !personCategories.length"
      color="success"
      outline
      @click="addNewPeriod"
    >
      {{ $t('ski.skiPricePeriods.new') }}
    </v-btn>
    <v-btn
      v-if="!personCategories.length && !personCategoriesLoading"
      :to="`/ski/ski-pass/season/${season.id}/person-categories`"
      color="warning"
      outline
    >
      {{ $t('ski.skiPersonCategories.addMissingPersonCategories') }}
    </v-btn>
    <v-btn
      :loading="periodsLoading"
      icon
      @click="loadPeriods(season.id)"
    >
      <v-icon>cached</v-icon>
    </v-btn>
    <div class="text-center pa-1">
      <v-progress-linear
        v-if="periodsLoading
          || pricesLoading
          || periodsSaveLoading
          || pricesSaveLoading"
        :indeterminate="true"
      />
    </div>
    <v-layout>
      <v-flex sm12 xs12>
        <h3>
          {{ $t('ski.supplier-currency') }}:
          <template v-if="season.supplier">
            {{ season.supplier.currency }}
            ({{ season.supplier.name }})
          </template>
        </h3>
      </v-flex>
    </v-layout>
    <div v-if="activePricePeriods.length" class="pa-3">
      <h3>
        {{ $t('ski.skiPricePeriods.currentlyPeriods') }}
        ({{ Object.keys(activePricePeriods).length }})
      </h3>
      <div class="pa-3">
        <v-alert
          :value="errorOccurred"
          color="error"
          icon="warning"
          outline
        >
          <span v-html="errorOccurred"></span>
        </v-alert>
        <div
          v-for="(period, periodId) in activePricePeriods"
          :key="'active-ski-periods-' + periodId"
          xs12
        >
          <ski-price-period
            :period-id="period.id"
            :season="season"
            :person-categories="personCategories"
            :period="period"
            :period-loading="periodsLoading"
            :save-period-loading="periodsSaveLoading"
            :save-period-errpr="periodsSaveError"
            :prices-loading="pricesLoading"
            :save-price-loading="pricesSaveLoading"
            :save-price-error="pricesSaveError"
            @onChange="onPeriodChange"
            @loadPricesByPeriodId="loadPricesByPeriodId"
            @createOrUpdatePeriod="createOrUpdatePeriod"
            @createPeriodToAnotherPeriod="createPeriodToAnotherPeriod"
            @removePeriodByPeriodId="removePeriodByPeriodId"
            @onPriceChange="onPriceChange"
            @addNewPrice="addNewPrice"
            @createOrUpdatePrice="createOrUpdatePrice"
            @removePricesInDay="removePricesInDay"
            @removePriceByPriceId="removePriceByPriceId"
          />
        </div>
      </div>
    </div>
    <v-divider v-if="historicallyPricePeriods.length" />
    <div v-if="historicallyPricePeriods.length" class="pa-3">
      <h3 class="d-inline-block">
        {{ $t('ski.skiPricePeriods.oldPeriods') }}
        ({{ Object.keys(historicallyPricePeriods).length }})
      </h3>
      <v-btn
        @click="showOldPeriodPrices = !showOldPeriodPrices"
        :color="showOldPeriodPrices ? 'warning' : 'success'"
        small
      >
        {{ hideOrShowOldPeriodsButtonText }}
      </v-btn>
      <div class="pa-3" v-if="showOldPeriodPrices">
        <v-layout
          v-for="(period, periodId) in historicallyPricePeriods"
          :key="'old-ski-periods-' + periodId"
          xs12
        >
          <ski-price-period
            :period-id="period.id"
            :season="season"
            :person-categories="personCategories"
            :period="period"
            :period-loading="periodsLoading"
            :save-period-loading="periodsSaveLoading"
            :save-period-errpr="periodsSaveError"
            :prices-loading="pricesLoading"
            :save-price-loading="pricesSaveLoading"
            :save-price-error="pricesSaveError"
            @onChange="onPeriodChange"
            @loadPricesByPeriodId="loadPricesByPeriodId"
            @createOrUpdatePeriod="createOrUpdatePeriod"
            @createPeriodToAnotherPeriod="createPeriodToAnotherPeriod"
            @removePeriodByPeriodId="removePeriodByPeriodId"
            @onPriceChange="onPriceChange"
            @addNewPrice="addNewPrice"
            @createOrUpdatePrice="createOrUpdatePrice"
            @removePricesInDay="removePricesInDay"
            @removePriceByPriceId="removePriceByPriceId"
          />
        </v-layout>
      </div>
    </div>
  </div>
</template>



// WEBPACK FOOTER //
// src/pages/ski/skipass/SkiPassSeasonPricePeriods.vue